import React, {
  useState,
  useMemo,
  useCallback,
  useRef,
  useEffect,
} from "react";
import { useNavigate } from "react-router";
import { useBasePath } from "../../contexts/BasePathContext";
import { useAppContext } from "../../contexts/AppContext";
import { useDemo } from "../../contexts/DemoContext";
import { useFieldConfig } from "../../contexts/FieldConfigContext";
import { useFeatureAccess } from "../../contexts/LicenseContext";
import { useUndoRedo } from "../../hooks/useUndoRedo";
import { useFiles } from "../../hooks/useFiles";
import { useSelection } from "../../hooks/useSelection";
import { useIsMobile, useIsTablet } from "../../hooks/useMobile";
import { useFileListProjection } from "../../hooks/useFileListProjection";
import { EXIF_FIELDS } from "../../config/fieldDefinitions";
import { validateFieldValue } from "../../utils/fieldValidation";
import { areDateTakenEqual } from "../../utils/dateTaken";
import SelectionToolbar from "./SelectionToolbar";
import {
  Checkbox,
  Button,
  Alert,
  AlertAction,
  AlertDescription,
  AlertTitle,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  toast,
} from "@exiftrator/shared/components/ui";
import { Thumbnail } from "../Thumbnail";
import { TableToolbar } from "./TableToolbar";
import FileListControls from "../FileQueue/FileListControls";
import { X } from "lucide-react";
import { getMetadataActionDialogText } from "../common/metadataActionDialogCopy";
import EditableCell from "./EditableCell";
import TableCell from "./TableCell";
import {
  getSelectedFileIndicesFromRange,
  getCellRangeEdges,
  isCellInRange,
  normalizeCellRange,
} from "./tableSelection";
import {
  parseClipboardTsv,
  serializeCellRangeToTsv,
} from "./tableClipboard";
import {
  applySnapshotByIndex,
  createFileSnapshot,
  createSnapshotByIndices,
  patchFileFields,
} from "./tableFileState";
import {
  WORKSPACE_SPACING,
  getDensityClass,
} from "../Workspace/workspaceSpacing";

function TableView() {
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();
  const statusStripInsetXClass = getDensityClass(
    isMobile,
    isTablet,
    WORKSPACE_SPACING.statusStripInsetX,
  );
  const navigate = useNavigate();
  const { appPath } = useBasePath();
  const { isDemo } = useDemo();
  const {
    files,
    setFiles,
    setPrimaryIndex,
    setSelectedCellRange,
    setSelectedIndices,
    setLocationModalOpen,
    setPresetModalOpen,
    setKeyboardShortcutsModalOpen,
    setKeyboardShortcutsInitialView,
    focusedCell,
    setFocusedCell,
    selectionAnchor,
    setSelectionAnchor,
    selectionEnd,
    setSelectionEnd,
    fileListSort,
    setFileListSort,
    fileListFilters,
    setFileListFilters,
    setTableVisibleRowMap,
  } = useAppContext();
  const { removeFiles } = useFiles();
  const fieldAccess = useFeatureAccess("fieldCustomization");
  const { recordAction } = useUndoRedo();
  const {
    getEnabledFieldsOrdered,
    updateFieldEnabled,
    isFieldEnabled,
    config,
  } = useFieldConfig();
  const {
    selectedIndices,
    toggleSelect,
    clearSelection,
    isSelected,
  } = useSelection();
  const [editingCell, setEditingCell] = useState(null);
  const [bulkEditMode, setBulkEditMode] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [inputRejection, setInputRejection] = useState(null);
  const [highlightedRowIndex, setHighlightedRowIndex] = useState(null); // For bulk edit mode hover/keyboard highlight
  // Track copied cell range for visual feedback (dashed border)
  const [copiedCellRange, setCopiedCellRange] = useState(null); // { minRow, maxRow, minField, maxField } or null
  const tableRef = useRef(null);
  const isExitingEditRef = useRef(false); // Flag to prevent blur from clearing focus
  const { visibleIndices } = useFileListProjection({
    files,
    sort: fileListSort,
    filters: fileListFilters,
  });

  const visibleRowCount = visibleIndices.length;
  const toFileIndex = useCallback(
    (rowIndex) => visibleIndices[rowIndex] ?? -1,
    [visibleIndices],
  );

  const clearCellSelection = useCallback(() => {
    setSelectionAnchor(null);
    setSelectionEnd(null);
    setSelectedCellRange(null);
  }, [setSelectionAnchor, setSelectionEnd, setSelectedCellRange]);

  const startCellEditing = useCallback((rowIndex, fieldId, options = {}) => {
    const { initialValue = null, replaceExisting = false } = options;
    setEditingCell({
      rowIndex,
      field: fieldId,
      initialValue,
      replaceExisting,
    });
  }, []);

  const showInputRejection = useCallback((message) => {
    setInputRejection({
      message,
      timestamp: Date.now(),
    });
  }, []);

  const applySnapshot = useCallback(
    (snapshotByIndex) => {
      setFiles((prev) => applySnapshotByIndex(prev, snapshotByIndex));
    },
    [setFiles],
  );

  useEffect(() => {
    setTableVisibleRowMap(visibleIndices);
  }, [visibleIndices, setTableVisibleRowMap]);

  // Get enabled fields in category order
  const enabledFields = useMemo(
    () => getEnabledFieldsOrdered(),
    [getEnabledFieldsOrdered],
  );

  // All fields with their enabled state (for column visibility dropdown)
  const allTableFields = useMemo(() => {
    const fields = [];
    config.categoryOrder.forEach((categoryId) => {
      const categoryFields = Object.values(EXIF_FIELDS).filter(
        (field) => field.category === categoryId,
      );
      fields.push(
        ...categoryFields.map((field) => ({
          ...field,
          enabled: isFieldEnabled(field.id),
        })),
      );
    });
    return fields;
  }, [isFieldEnabled, config.categoryOrder]);

  // Navigable field IDs (for keyboard navigation)
  const navigableFields = useMemo(
    () => enabledFields.map((f) => f.id),
    [enabledFields],
  );

  // Ensure a cell is always focused - initialize to first cell if none focused
  // Since focusedCell is in context, it persists across view switches
  const hasInitializedFocusRef = useRef(false);
  const prevFocusedCellRef = useRef(null);

  useEffect(() => {
    // Only initialize if not already set (first load or after files are cleared)
    if (
      !focusedCell &&
      !hasInitializedFocusRef.current &&
      visibleRowCount > 0 &&
      navigableFields.length > 0
    ) {
      setFocusedCell({ rowIndex: 0, fieldId: navigableFields[0] });
      hasInitializedFocusRef.current = true;
    }
    // Reset init flag if no files (allows re-init when files are loaded)
    if (visibleRowCount === 0) {
      hasInitializedFocusRef.current = false;
    }
  }, [visibleRowCount, navigableFields.length, focusedCell, setFocusedCell]);

  // Focus the table element on mount so keyboard navigation works immediately
  useEffect(() => {
    tableRef.current?.focus();
  }, []);

  // Track whether we've scrolled on this mount
  const hasScrolledOnMountRef = useRef(false);

  // Auto-scroll focused cell into view
  useEffect(() => {
    if (!focusedCell) return;

    const cellSelector = `[data-row="${focusedCell.rowIndex}"][data-field="${focusedCell.fieldId}"]`;
    const cellElement = tableRef.current?.querySelector(cellSelector);

    if (!cellElement) return;

    if (!hasScrolledOnMountRef.current) {
      // First time on this mount: scroll to center (better for returning from editor view)
      cellElement.scrollIntoView({
        behavior: "auto",
        block: "center",
        inline: "nearest",
      });
      hasScrolledOnMountRef.current = true;
    } else if (prevFocusedCellRef.current !== null) {
      // Subsequent focus changes: scroll minimally
      cellElement.scrollIntoView({
        behavior: "auto",
        block: "nearest",
        inline: "nearest",
      });
    }

    prevFocusedCellRef.current = focusedCell;
  }, [focusedCell]);

  // Reset scroll flag when row projection changes.
  useEffect(() => {
    hasScrolledOnMountRef.current = false;
    prevFocusedCellRef.current = null;
  }, [visibleRowCount]);

  // Compute selected cells from rectangular selection
  const selectedCellRange = useMemo(() => {
    return normalizeCellRange(selectionAnchor, selectionEnd);
  }, [selectionAnchor, selectionEnd]);

  // Sync cell selection range to context for preset modal
  useEffect(() => {
    setSelectedCellRange(selectedCellRange);
  }, [selectedCellRange, setSelectedCellRange]);

  // Pre-compute cell states for performance (HIGH IMPACT)
  // This replaces per-cell function calls during render with a single O(n*m) computation
  const cellStates = useMemo(() => {
    const states = new Map();
    visibleIndices.forEach((_, rowIndex) => {
      navigableFields.forEach((fieldId, fieldIndex) => {
        const isInSelection =
          isCellInRange(selectedCellRange, rowIndex, fieldIndex);
        const selectionEdges = getCellRangeEdges(
          selectedCellRange,
          rowIndex,
          fieldIndex,
        );
        const copiedEdges = getCellRangeEdges(
          copiedCellRange,
          rowIndex,
          fieldIndex,
        );

        states.set(`${rowIndex}-${fieldId}`, {
          isInSelection,
          selectionEdges,
          copiedEdges,
        });
      });
    });
    return states;
  }, [visibleIndices, navigableFields, selectedCellRange, copiedCellRange]);

  // Get unique row indices from cell selection
  const selectedRowsFromCells = useMemo(() => {
    return getSelectedFileIndicesFromRange(selectedCellRange, toFileIndex);
  }, [selectedCellRange, toFileIndex]);

  // Sync file selection with cell range selection
  // When cells are selected, the corresponding rows should be selected too
  // This ensures that switching to Editor view shows the same files
  const prevCellRowsRef = useRef(new Set());
  useEffect(() => {
    // Only update when cell selection actually changes
    const prevCellRows = prevCellRowsRef.current;
    // Symmetric comparison: check if sets are different
    const cellRowsChanged =
      selectedRowsFromCells.size !== prevCellRows.size ||
      ![...selectedRowsFromCells].every((val) => prevCellRows.has(val)) ||
      ![...prevCellRows].every((val) => selectedRowsFromCells.has(val));

    if (cellRowsChanged && selectedRowsFromCells.size > 0) {
      setSelectedIndices(selectedRowsFromCells);
      // Update primary index to the first row in selection
      const firstRow = Math.min(...selectedRowsFromCells);
      setPrimaryIndex(firstRow);
    }

    prevCellRowsRef.current = selectedRowsFromCells;
  }, [selectedRowsFromCells, setSelectedIndices, setPrimaryIndex]);

  // Focus-first cell click: click once to focus, click again to edit
  // In bulk edit mode, individual cell selection is disabled
  const handleCellClick = useCallback(
    (rowIndex, fieldId, event) => {
      // In bulk edit mode, don't allow individual cell selection
      if (bulkEditMode) return;

      const fieldIndex = navigableFields.indexOf(fieldId);
      if (fieldIndex === -1) return;
      const fileIndex = toFileIndex(rowIndex);
      if (fileIndex < 0) return;

      // Focus the table for keyboard navigation
      tableRef.current?.focus();

      if (event?.shiftKey) {
        const targetCell = { rowIndex, fieldIndex };
        let anchor = selectionAnchor;
        if (!anchor && focusedCell) {
          const focusedFieldIndex = navigableFields.indexOf(focusedCell.fieldId);
          if (focusedFieldIndex !== -1) {
            anchor = {
              rowIndex: focusedCell.rowIndex,
              fieldIndex: focusedFieldIndex,
            };
          }
        }
        if (!anchor) {
          anchor = targetCell;
        }

        if (!selectionAnchor) {
          setSelectionAnchor(anchor);
        }
        setSelectionEnd(targetCell);
        setFocusedCell({ rowIndex, fieldId });
        return;
      }

      clearCellSelection();

      if (
        focusedCell?.rowIndex === rowIndex &&
        focusedCell?.fieldId === fieldId
      ) {
        // Already focused, start editing
        setEditingCell({ rowIndex, field: fieldId });
      } else {
        // Set focus to new cell
        setFocusedCell({ rowIndex, fieldId });
        // Also update file selection so location/preset modals use the correct file
        setSelectedIndices(new Set([fileIndex]));
        setPrimaryIndex(fileIndex);
      }
    },
    [
      focusedCell,
      bulkEditMode,
      navigableFields,
      selectionAnchor,
      setSelectionAnchor,
      setSelectionEnd,
      clearCellSelection,
      setSelectedIndices,
      setPrimaryIndex,
      toFileIndex,
    ],
  );

  // Copy focused cell value or selected range
  const handleCopy = useCallback(() => {
    if (!focusedCell) return;

    // If we have a range, copy as TSV
    if (selectedCellRange) {
      navigator.clipboard.writeText(
        serializeCellRangeToTsv({
          files,
          navigableFields,
          toFileIndex,
          range: selectedCellRange,
        }),
      );
      setCopiedCellRange({ ...selectedCellRange });
    } else {
      // Single cell copy
      const focusedFileIndex = toFileIndex(focusedCell.rowIndex);
      if (focusedFileIndex < 0) return;
      const file = files[focusedFileIndex];
      if (!file) return;
      const value = file.modifiedExifData[focusedCell.fieldId];
      navigator.clipboard.writeText(String(value ?? ""));
      // Store single cell as copied range
      const fieldIndex = navigableFields.indexOf(focusedCell.fieldId);
      setCopiedCellRange({
        minRow: focusedCell.rowIndex,
        maxRow: focusedCell.rowIndex,
        minField: fieldIndex,
        maxField: fieldIndex,
      });
    }
  }, [focusedCell, selectedCellRange, files, navigableFields, toFileIndex]);

  // Paste to focused cell and all selected cells (supports TSV)
  // Called from native paste event - receives clipboard text directly
  const handlePaste = useCallback(
    (clipboardText) => {
      if (!focusedCell) return;
      if (!clipboardText) return;
      const rows = parseClipboardTsv(clipboardText);
      if (rows.length === 0) return;

      const srcRows = rows.length;
      const srcCols = rows.reduce((maxCols, row) => Math.max(maxCols, row.length), 0);
      if (srcCols === 0) return;

      const anchorRow = selectedCellRange
        ? selectedCellRange.minRow
        : focusedCell.rowIndex;
      const anchorFieldIdx = selectedCellRange
        ? selectedCellRange.minField
        : navigableFields.indexOf(focusedCell.fieldId);
      if (anchorFieldIdx < 0) return;

      const selectedRows = selectedCellRange
        ? selectedCellRange.maxRow - selectedCellRange.minRow + 1
        : 1;
      const selectedCols = selectedCellRange
        ? selectedCellRange.maxField - selectedCellRange.minField + 1
        : 1;
      const targetRows =
        selectedCellRange && srcRows === 1 ? selectedRows : srcRows;
      const targetCols =
        selectedCellRange && srcCols === 1 ? selectedCols : srcCols;

      const candidateUpdates = []; // Array of { rowIndex, fileIndex, fieldId, value }
      for (let rOffset = 0; rOffset < targetRows; rOffset++) {
        const rowIndex = anchorRow + rOffset;
        if (rowIndex >= visibleRowCount) break;
        const fileIndex = toFileIndex(rowIndex);
        if (fileIndex < 0) continue;

        const sourceRowIndex = srcRows === 1 ? 0 : rOffset;
        if (sourceRowIndex >= srcRows) continue;
        const sourceRow = rows[sourceRowIndex] ?? [];

        for (let cOffset = 0; cOffset < targetCols; cOffset++) {
          const fieldIndex = anchorFieldIdx + cOffset;
          if (fieldIndex >= navigableFields.length) break;

          const sourceColIndex = srcCols === 1 ? 0 : cOffset;
          if (sourceColIndex >= sourceRow.length) continue; // Ragged row support

          candidateUpdates.push({
            rowIndex,
            fileIndex,
            fieldId: navigableFields[fieldIndex],
            value: sourceRow[sourceColIndex],
          });
        }
      }

      if (candidateUpdates.length === 0) return;

      const updates = []; // Array of { fileIndex, fieldId, processedValue }
      let skippedInvalidCount = 0;
      candidateUpdates.forEach((update) => {
        const validation = validateFieldValue(update.fieldId, update.value);
        if (validation.valid) {
          updates.push({
            fileIndex: update.fileIndex,
            fieldId: update.fieldId,
            processedValue: validation.processedValue,
          });
          return;
        }
        skippedInvalidCount++;
      });

      if (updates.length === 0) {
        if (skippedInvalidCount > 0) {
          toast.warning(
            `Paste skipped: ${skippedInvalidCount} invalid cell${skippedInvalidCount === 1 ? "" : "s"}.`,
          );
        }
        return;
      }

      // Group updates by file for efficient state update
      const updatesByRow = {};
      updates.forEach((u) => {
        if (!updatesByRow[u.fileIndex]) updatesByRow[u.fileIndex] = [];
        updatesByRow[u.fileIndex].push(u);
      });

      const rowsToUpdate = Object.keys(updatesByRow).map(Number);
      const fieldPatchesByRow = {};
      rowsToUpdate.forEach((rowIndex) => {
        fieldPatchesByRow[rowIndex] = Object.fromEntries(
          updatesByRow[rowIndex].map(({ fieldId, processedValue }) => [
            fieldId,
            processedValue,
          ]),
        );
      });

      const previousData = createSnapshotByIndices(files, rowsToUpdate);

      setFiles((prev) =>
        prev.map((f, i) => {
          if (fieldPatchesByRow[i]) {
            return patchFileFields(f, fieldPatchesByRow[i], {
              hasChanges: true,
            });
          }
          return f;
        }),
      );

      if (skippedInvalidCount > 0) {
        toast.info(
          `Pasted ${updates.length} cell${updates.length === 1 ? "" : "s"}; skipped ${skippedInvalidCount} invalid cell${skippedInvalidCount === 1 ? "" : "s"}.`,
        );
      }

      // Record Undo/Redo
      recordAction(
        `Paste ${updates.length} cell${updates.length > 1 ? "s" : ""}`,
        () => applySnapshot(previousData),
        () => {
          setFiles((prev) =>
            prev.map((f, i) => {
              if (fieldPatchesByRow[i]) {
                return patchFileFields(f, fieldPatchesByRow[i], {
                  hasChanges: true,
                });
              }
              return f;
            }),
          );
        },
        rowsToUpdate,
      );
    },
    [
      focusedCell,
      selectedCellRange,
      files,
      navigableFields,
      setFiles,
      recordAction,
      toFileIndex,
      visibleRowCount,
      applySnapshot,
    ],
  );

  // Handle delete/Backspace to clear selected cells
  const handleDelete = useCallback(() => {
    if (!focusedCell) return;

    // Determine which cells to clear
    let cellsToClear = [];
    if (selectedCellRange) {
      // Clear entire selection range
      for (
        let r = selectedCellRange.minRow;
        r <= selectedCellRange.maxRow;
        r++
      ) {
        for (
          let f = selectedCellRange.minField;
          f <= selectedCellRange.maxField;
          f++
        ) {
          cellsToClear.push({
            rowIndex: r,
            fieldId: navigableFields[f],
          });
        }
      }
    } else {
      // Clear just the focused cell
      cellsToClear.push({
        rowIndex: focusedCell.rowIndex,
        fieldId: focusedCell.fieldId,
      });
    }

    if (cellsToClear.length === 0) return;

    // Group by row for efficient update
    const cellsByRow = {};
    cellsToClear.forEach((c) => {
      const fileIndex = toFileIndex(c.rowIndex);
      if (fileIndex < 0) return;
      if (!cellsByRow[fileIndex]) cellsByRow[fileIndex] = [];
      cellsByRow[fileIndex].push(c.fieldId);
    });

    const rowsToUpdate = Object.keys(cellsByRow).map(Number);
    const previousData = createSnapshotByIndices(files, rowsToUpdate);
    const fieldPatchesByRow = {};
    rowsToUpdate.forEach((rowIndex) => {
      fieldPatchesByRow[rowIndex] = Object.fromEntries(
        cellsByRow[rowIndex].map((fieldId) => [fieldId, null]),
      );
    });

    setFiles((prev) =>
      prev.map((f, i) => {
        if (fieldPatchesByRow[i]) {
          return patchFileFields(f, fieldPatchesByRow[i], {
            hasChanges: true,
          });
        }
        return f;
      }),
    );

    // Record for undo/redo
    recordAction(
      `Clear ${cellsToClear.length} cell${cellsToClear.length > 1 ? "s" : ""}`,
      () => applySnapshot(previousData),
      () => {
        setFiles((prev) =>
          prev.map((f, i) => {
            if (fieldPatchesByRow[i]) {
              return patchFileFields(f, fieldPatchesByRow[i], {
                hasChanges: true,
              });
            }
            return f;
          }),
        );
      },
      rowsToUpdate,
    );
  }, [
    focusedCell,
    selectedCellRange,
    navigableFields,
    files,
    setFiles,
    recordAction,
    toFileIndex,
    applySnapshot,
  ]);

  // Keyboard navigation handler
  const handleTableKeyDown = useCallback(
    (e) => {
      // Don't handle if currently editing (let input handle keys)
      if (editingCell) return;

      // Handle copy regardless of focus
      if ((e.ctrlKey || e.metaKey) && e.key === "c") {
        e.preventDefault();
        handleCopy();
        return;
      }
      // Note: Paste is handled via native paste event (onPaste), not keyboard shortcut

      // Bulk edit mode: Up/Down arrows highlight rows, Enter selects highlighted row
      if (bulkEditMode) {
        if (e.key === "ArrowUp") {
          e.preventDefault();
          e.stopPropagation();
          setHighlightedRowIndex((prev) => {
            if (prev === null) return 0;
            return Math.max(0, prev - 1);
          });
          return;
        }
        if (e.key === "ArrowDown") {
          e.preventDefault();
          e.stopPropagation();
          setHighlightedRowIndex((prev) => {
            if (prev === null) return 0;
            return Math.min(visibleRowCount - 1, prev + 1);
          });
          return;
        }
        if (e.key === "Enter") {
          e.preventDefault();
          e.stopPropagation();
          if (
            highlightedRowIndex !== null &&
            highlightedRowIndex >= 0 &&
            highlightedRowIndex < visibleRowCount
          ) {
            const fileIndex = toFileIndex(highlightedRowIndex);
            if (fileIndex >= 0) {
              toggleSelect(fileIndex);
            }
          }
          return;
        }
      }

      if (!focusedCell) return;

      const { rowIndex, fieldId } = focusedCell;
      const fieldIndex = navigableFields.indexOf(fieldId);

      // Modifier key detection for enhanced navigation
      const isMod = e.metaKey || e.ctrlKey;

      // Helper to handle navigation with optional shift for selection
      const handleNavigation = (newRowIndex, newFieldIndex) => {
        if (newRowIndex < 0 || newRowIndex >= visibleRowCount) return;
        if (newFieldIndex < 0 || newFieldIndex >= navigableFields.length)
          return;
        const fileIndex = toFileIndex(newRowIndex);
        if (fileIndex < 0) return;

        const newFieldId = navigableFields[newFieldIndex];

        if (e.shiftKey) {
          // Extend selection
          if (!selectionAnchor) {
            // Start selection from current cell
            setSelectionAnchor({ rowIndex, fieldIndex });
          }
          setSelectionEnd({ rowIndex: newRowIndex, fieldIndex: newFieldIndex });
        } else {
          // Clear selection when moving without shift
          clearCellSelection();
          // Also update file selection so location/preset modals use the correct file
          setSelectedIndices(new Set([fileIndex]));
          setPrimaryIndex(fileIndex);
        }

        setFocusedCell({ rowIndex: newRowIndex, fieldId: newFieldId });
      };

      switch (e.key) {
        case "ArrowRight":
          // Skip for view switching (Alt + Arrow)
          if (e.altKey) return;
          e.preventDefault();
          e.stopPropagation();
          if (isMod) {
            // Jump to last column
            handleNavigation(rowIndex, navigableFields.length - 1);
          } else {
            handleNavigation(rowIndex, fieldIndex + 1);
          }
          break;
        case "ArrowLeft":
          // Skip for view switching (Alt + Arrow)
          if (e.altKey) return;
          e.preventDefault();
          e.stopPropagation();
          if (isMod) {
            // Jump to first column
            handleNavigation(rowIndex, 0);
          } else {
            handleNavigation(rowIndex, fieldIndex - 1);
          }
          break;
        case "ArrowDown":
          e.preventDefault();
          e.stopPropagation();
          if (isMod) {
            // Jump to last row
            handleNavigation(visibleRowCount - 1, fieldIndex);
          } else {
            handleNavigation(rowIndex + 1, fieldIndex);
          }
          break;
        case "ArrowUp":
          e.preventDefault();
          e.stopPropagation();
          if (isMod) {
            // Jump to first row
            handleNavigation(0, fieldIndex);
          } else {
            handleNavigation(rowIndex - 1, fieldIndex);
          }
          break;
        case "Delete":
        case "Backspace":
          e.preventDefault();
          handleDelete();
          // Restore focus to table after delete (re-render may have removed focus)
          requestAnimationFrame(() => {
            tableRef.current?.focus();
          });
          break;
        case "Enter":
          e.preventDefault();
          if (!bulkEditMode) {
            startCellEditing(rowIndex, fieldId);
          }
          break;
        case "Escape":
          e.preventDefault();
          // Clear copied range, then selection - but keep focus on cell
          if (copiedCellRange) {
            setCopiedCellRange(null);
          } else if (selectionAnchor) {
            clearCellSelection();
          }
          // Don't clear focusedCell - a cell should always be focused
          break;
        case "Tab":
          e.preventDefault();
          e.stopPropagation();
          const nextIndex = e.shiftKey ? fieldIndex - 1 : fieldIndex + 1;
          if (nextIndex >= 0 && nextIndex < navigableFields.length) {
            clearCellSelection();
            setFocusedCell({ rowIndex, fieldId: navigableFields[nextIndex] });
          }
          break;
        default:
          // Start typing = begin editing (printable character, no modifier)
          // Not allowed in bulk edit mode
          if (
            !bulkEditMode &&
            e.key.length === 1 &&
            !e.ctrlKey &&
            !e.metaKey &&
            !e.altKey
          ) {
            e.preventDefault();
            startCellEditing(rowIndex, fieldId, {
              initialValue: e.key,
              replaceExisting: true,
            });
          }
      }
    },
    [
      focusedCell,
      editingCell,
      navigableFields,
      visibleRowCount,
      handleCopy,
      handleDelete,
      bulkEditMode,
      startCellEditing,
      highlightedRowIndex,
      toggleSelect,
      selectionAnchor,
      clearCellSelection,
      copiedCellRange,
      toFileIndex,
    ],
  );

  // Handle paste event from browser (avoids clipboard permission prompts)
  const handleTablePaste = useCallback(
    (e) => {
      // Don't handle if currently editing (let input handle paste)
      if (editingCell) return;

      e.preventDefault();
      const clipboardText = e.clipboardData?.getData("text/plain");
      if (clipboardText) {
        handlePaste(clipboardText);
      }
    },
    [editingCell, handlePaste],
  );

  const handleTableBlur = useCallback(() => {
    if (isExitingEditRef.current) {
      isExitingEditRef.current = false;
    }
  }, []);

  // Helper to exit edit mode and refocus the table
  const exitEditMode = useCallback((rowIndex, fieldId) => {
    isExitingEditRef.current = true;
    setEditingCell(null);
    setFocusedCell({ rowIndex, fieldId });
    // Use setTimeout to ensure focus happens after blur event completes
    setTimeout(() => {
      tableRef.current?.focus();
      isExitingEditRef.current = false;
    }, 0);
  }, []);

  const handleThumbnailClick = useCallback(
    (fileIndex) => {
      setPrimaryIndex(fileIndex);
      navigate(appPath("/editor"));
    },
    [setPrimaryIndex, navigate, appPath],
  );

  const areFieldValuesEqual = useCallback((fieldId, left, right) => {
    if (fieldId === "dateTaken") {
      return areDateTakenEqual(left, right);
    }
    return left === right;
  }, []);

  const isFieldModified = useCallback((file, field) => {
    if (!file.hasChanges) return false;
    return !areFieldValuesEqual(field, file.modifiedExifData[field], file.exifData[field]);
  }, [areFieldValuesEqual]);

  const hasAnyChanges = useCallback((file) => {
    // Check all fields in modifiedExifData vs exifData
    const keys = new Set([
      ...Object.keys(file.modifiedExifData || {}),
      ...Object.keys(file.exifData || {}),
    ]);
    for (const key of keys) {
      if (!areFieldValuesEqual(key, file.modifiedExifData?.[key], file.exifData?.[key])) {
        return true;
      }
    }
    return false;
  }, [areFieldValuesEqual]);

  const handleCellSave = useCallback(
    (rowIndex, field, newValue) => {
      const fileIndex = toFileIndex(rowIndex);
      if (fileIndex < 0) {
        exitEditMode(rowIndex, field);
        return;
      }
      const file = files[fileIndex];
      if (!file) {
        exitEditMode(rowIndex, field);
        return;
      }
      const oldValue = file.modifiedExifData[field];

      // Don't save if value hasn't changed
      if (oldValue === newValue) {
        exitEditMode(rowIndex, field);
        return;
      }

      // Validate and process value
      const validation = validateFieldValue(field, newValue);

      if (!validation.valid) {
        showInputRejection(validation.error);
        exitEditMode(rowIndex, field);
        return;
      }

      const processedValue = validation.processedValue;
      if (areFieldValuesEqual(field, oldValue, processedValue)) {
        exitEditMode(rowIndex, field);
        return;
      }

      setInputRejection(null);

      // Update files
      const fieldPatch = { [field]: processedValue };

      setFiles((prev) =>
        prev.map((f, i) =>
          i === fileIndex
            ? patchFileFields(f, fieldPatch, {
                resolveHasChanges: hasAnyChanges,
              })
            : f,
        ),
      );

      // Record for undo/redo
      recordAction(
        `Edit ${field}`,
        () => {
          setFiles((prev) =>
            prev.map((f, i) => {
              if (i === fileIndex) {
                return patchFileFields(
                  f,
                  { [field]: oldValue },
                  { resolveHasChanges: hasAnyChanges },
                );
              }
              return f;
            }),
          );
        },
        () => {
          setFiles((prev) =>
            prev.map((f, i) => {
              if (i === fileIndex) {
                return patchFileFields(
                  f,
                  fieldPatch,
                  { resolveHasChanges: hasAnyChanges },
                );
              }
              return f;
            }),
          );
        },
        [fileIndex],
      );

      exitEditMode(rowIndex, field);
    },
    [
      toFileIndex,
      files,
      setFiles,
      recordAction,
      exitEditMode,
      showInputRejection,
      hasAnyChanges,
      areFieldValuesEqual,
    ],
  );

  // Bulk edit handler
  const handleBulkEditField = useCallback(
    (fieldId, value) => {
      const indices = Array.from(selectedIndices);
      if (indices.length === 0) return;

      // Validate value
      const validation = validateFieldValue(fieldId, value);
      if (!validation.valid) {
        showInputRejection(validation.error);
        return;
      }

      const processedValue = validation.processedValue;
      setInputRejection(null);
      const previousData = createSnapshotByIndices(files, indices);
      const fieldPatch = { [fieldId]: processedValue };

      setFiles((prev) =>
        prev.map((f, i) => {
          if (selectedIndices.has(i)) {
            return patchFileFields(f, fieldPatch, { hasChanges: true });
          }
          return f;
        }),
      );

      // Record for undo/redo
      recordAction(
        `Edit ${fieldId} on ${indices.length} files`,
        () => applySnapshot(previousData),
        () => {
          setFiles((prev) =>
            prev.map((f, i) => {
              if (selectedIndices.has(i)) {
                return patchFileFields(f, fieldPatch, { hasChanges: true });
              }
              return f;
            }),
          );
        },
        indices,
      );
    },
    [selectedIndices, files, setFiles, recordAction, showInputRejection],
  );

  // Checkbox click handler
  const handleCheckboxChange = useCallback(
    (rowIndex, e) => {
      e.stopPropagation();
      const fileIndex = toFileIndex(rowIndex);
      if (fileIndex >= 0) {
        toggleSelect(fileIndex);
      }
    },
    [toggleSelect, toFileIndex],
  );

  // Row click handler for bulk edit mode
  const handleRowClick = useCallback(
    (rowIndex, e) => {
      if (bulkEditMode) {
        e.stopPropagation();
        const fileIndex = toFileIndex(rowIndex);
        if (fileIndex >= 0) {
          toggleSelect(fileIndex);
        }
      }
    },
    [bulkEditMode, toggleSelect, toFileIndex],
  );

  // Column header click handler - selects entire column
  const handleColumnHeaderClick = useCallback(
    (fieldId) => {
      const fieldIndex = navigableFields.indexOf(fieldId);
      if (fieldIndex === -1 || visibleRowCount === 0) return;

      // Set selection to entire column
      setSelectionAnchor({ rowIndex: 0, fieldIndex });
      setSelectionEnd({ rowIndex: visibleRowCount - 1, fieldIndex });

      // Focus the table for keyboard navigation
      tableRef.current?.focus();

      // Set focused cell to first cell in column
      setFocusedCell({ rowIndex: 0, fieldId });
    },
    [navigableFields, visibleRowCount],
  );

  // Header checkbox state
  const isAllSelected =
    visibleIndices.length > 0 &&
    visibleIndices.every((index) => selectedIndices.has(index));
  const isSomeSelected =
    visibleIndices.some((index) => selectedIndices.has(index)) && !isAllSelected;

  // Computed selection counts for toolbar
  const selectedCellCount = selectedCellRange
    ? (selectedCellRange.maxRow - selectedCellRange.minRow + 1) *
      (selectedCellRange.maxField - selectedCellRange.minField + 1)
    : 0;
  const selectedVisibleFileIndices = useMemo(
    () => visibleIndices.filter((index) => selectedIndices.has(index)),
    [visibleIndices, selectedIndices],
  );
  const selectedVisibleRowCount = selectedVisibleFileIndices.length;
  const tableFooterLabel = useMemo(
    () =>
      [
        `Showing ${visibleIndices.length} of ${files.length} file${files.length === 1 ? "" : "s"}`,
        `${selectedVisibleRowCount} selected`,
      ].join(" \u2022 "),
    [visibleIndices.length, files.length, selectedVisibleRowCount],
  );

  const handleToggleBulkEdit = () => {
    const newMode = !bulkEditMode;
    setBulkEditMode(newMode);
    clearCellSelection();
    setCopiedCellRange(null);
    setHighlightedRowIndex(null);
    if (!newMode) {
      clearSelection();
    }
  };

  const handleOpenPreset = () => {
    setPresetModalOpen(true);
  };

  const handleToggleColumnVisibility = (fieldId) => {
    if (!fieldAccess.allowed) {
      toast.info("Field customization is a Pro feature. Unlock Pro in Settings > License.");
      return;
    }

    // Toggle column visibility - the checkbox will pass the new state
    const field = EXIF_FIELDS[fieldId];
    if (field) {
      const currentEnabled = isFieldEnabled(fieldId);
      updateFieldEnabled(fieldId, !currentEnabled);
    }
  };

  const handleOpenShortcuts = () => {
    setKeyboardShortcutsInitialView("table");
    setKeyboardShortcutsModalOpen(true);
  };

  const applyMetadataActionToSelection = useCallback(
    (resolveNextValue, actionLabel) => {
      if (selectedVisibleFileIndices.length === 0 || enabledFields.length === 0) {
        return;
      }

      const editableFieldIds = enabledFields.map((field) => field.id);
      const previousData = {};
      const nextData = {};
      const changedIndices = [];

        selectedVisibleFileIndices.forEach((fileIndex) => {
          const file = files[fileIndex];
          if (!file) return;

        const nextModifiedExifData = { ...file.modifiedExifData };
        editableFieldIds.forEach((fieldId) => {
          nextModifiedExifData[fieldId] = resolveNextValue(file, fieldId);
        });

        const hasFieldChanges = editableFieldIds.some(
          (fieldId) =>
            !areFieldValuesEqual(
              fieldId,
              file.modifiedExifData?.[fieldId],
              nextModifiedExifData[fieldId],
            ),
        );
        const nextFile = {
          ...file,
          modifiedExifData: nextModifiedExifData,
        };
        const nextHasChanges = hasAnyChanges(nextFile);

        if (!hasFieldChanges && nextHasChanges === file.hasChanges) {
          return;
        }

        previousData[fileIndex] = createFileSnapshot(file);
        nextData[fileIndex] = createFileSnapshot({
          ...file,
          modifiedExifData: nextModifiedExifData,
          hasChanges: nextHasChanges,
        });
        changedIndices.push(fileIndex);
      });

      if (changedIndices.length === 0) return;

      applySnapshot(nextData);
      recordAction(
        actionLabel,
        () => applySnapshot(previousData),
        () => applySnapshot(nextData),
        changedIndices,
      );
    },
    [
      selectedVisibleFileIndices,
      enabledFields,
      files,
      setFiles,
      recordAction,
      areFieldValuesEqual,
      hasAnyChanges,
      applySnapshot,
    ],
  );

  const handleRestoreOriginalMetadata = useCallback(() => {
    if (selectedVisibleRowCount === 0) return;
    applyMetadataActionToSelection(
      (file, fieldId) => file.exifData?.[fieldId] ?? null,
      `Restore original metadata for ${selectedVisibleRowCount} file${selectedVisibleRowCount === 1 ? "" : "s"}`,
    );
  }, [selectedVisibleRowCount, applyMetadataActionToSelection]);

  const handleClearAllMetadata = useCallback(() => {
    if (selectedVisibleRowCount === 0) return;
    applyMetadataActionToSelection(
      () => null,
      `Clear all metadata for ${selectedVisibleRowCount} file${selectedVisibleRowCount === 1 ? "" : "s"}`,
    );
  }, [selectedVisibleRowCount, applyMetadataActionToSelection]);

  const metadataConfirmText = confirmAction
    ? getMetadataActionDialogText(confirmAction, selectedVisibleRowCount)
    : null;
  const confirmMeta = metadataConfirmText
    ? {
        ...metadataConfirmText,
        confirmClassName: metadataConfirmText.destructive
          ? "bg-destructive text-white hover:bg-destructive/90"
          : "",
        onConfirm:
          confirmAction === "restore"
            ? handleRestoreOriginalMetadata
            : handleClearAllMetadata,
      }
    : null;

  const handleSelectAllVisible = useCallback(() => {
    if (isAllSelected) {
      clearSelection();
      return;
    }

    const nextSelection = new Set(visibleIndices);
    setSelectedIndices(nextSelection);
    setPrimaryIndex(visibleIndices[0] ?? -1);
  }, [
    isAllSelected,
    clearSelection,
    visibleIndices,
    setSelectedIndices,
    setPrimaryIndex,
  ]);

  if (files.length === 0) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center bg-bg-workspace text-text-supporting p-8">
        <p className="text-[14px]">
          No files loaded. Drag and drop images to get started.
        </p>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col bg-bg-workspace overflow-hidden">
      {/* Modern Toolbar */}
      <TableToolbar
        bulkEditMode={bulkEditMode}
        selectedCellCount={selectedCellCount}
        selectedRowCount={selectedVisibleRowCount}
        onToggleBulkEdit={handleToggleBulkEdit}
        onOpenLocation={() => setLocationModalOpen(true)}
        onOpenPreset={handleOpenPreset}
        onRestoreOriginalMetadata={() => setConfirmAction("restore")}
        onClearAllMetadata={() => setConfirmAction("clear")}
        metadataActionsDisabled={selectedVisibleRowCount === 0}
        onToggleColumnVisibility={handleToggleColumnVisibility}
        onOpenShortcuts={handleOpenShortcuts}
        allFields={allTableFields}
        canCustomizeFields={fieldAccess.allowed}
        listControls={
          <FileListControls
            sort={fileListSort}
            onSortChange={setFileListSort}
            filters={fileListFilters}
            onFiltersChange={setFileListFilters}
          />
        }
      />
      {inputRejection && (
        <div className={`border-b border-border-default ${statusStripInsetXClass} py-3`}>
          <Alert key={inputRejection.timestamp} variant="destructive">
            <AlertTitle>Input rejected</AlertTitle>
            <AlertDescription>
              The value was not applied because it is invalid for this field: {inputRejection.message}
            </AlertDescription>
            <AlertAction onClick={() => setInputRejection(null)}>
              Dismiss
            </AlertAction>
          </Alert>
        </div>
      )}
      <div className="flex-1 overflow-auto">
        <table
          ref={tableRef}
          className="w-full border-separate border-spacing-0 outline-none"
          tabIndex={0}
          onKeyDown={handleTableKeyDown}
          onPaste={handleTablePaste}
          onBlur={handleTableBlur}
        >
          {/* Conditionally render selection toolbar or regular header (only in bulk edit mode with selection) */}
          {bulkEditMode ? (
            <SelectionToolbar
              selectedCount={selectedVisibleRowCount}
              enabledFields={enabledFields}
              fields={enabledFields}
              files={files}
              selectedIndices={selectedIndices}
              onEditField={handleBulkEditField}
              isAllSelected={isAllSelected}
              isSomeSelected={isSomeSelected}
              onSelectAll={handleSelectAllVisible}
            />
          ) : (
            <thead className="sticky top-0 z-20">
              <tr className="bg-bg-tertiary">
                <th className="px-4 py-2 text-left text-[11px] font-bold uppercase tracking-wider text-text-secondary border-b border-r border-border-default min-w-[132px]">
                  Preview
                </th>
                <th className="px-4 py-2 text-left text-[11px] font-bold uppercase tracking-wider text-text-secondary border-b border-r border-border-default min-w-[150px]">
                  Filename
                </th>
                {enabledFields.map((field) => (
                  <th
                    key={field.id}
                    onClick={() => handleColumnHeaderClick(field.id)}
                    className={`px-4 py-2 text-left text-[11px] font-bold uppercase tracking-wider text-text-secondary border-b border-r border-border-default min-w-[120px] cursor-pointer hover:bg-bg-secondary transition-colors ${field.category === "location" ? "text-accent-primary" : ""}`}
                  >
                    {field.label}
                  </th>
                ))}
                <th className="px-4 py-2 text-left text-[11px] font-bold uppercase tracking-wider text-text-secondary border-b border-r border-border-default w-[60px]"></th>
              </tr>
            </thead>
          )}
          <tbody>
            {visibleIndices.length === 0 && (
              <tr>
                <td
                  colSpan={enabledFields.length + (bulkEditMode ? 4 : 3)}
                  className="px-4 py-8 text-center text-sm text-text-supporting"
                >
                  No files match current filters.
                </td>
              </tr>
            )}
            {visibleIndices.map((fileIndex, rowIndex) => {
              const file = files[fileIndex];

              return (
                <tr
                  key={fileIndex}
                  style={{
                    contentVisibility: "auto",
                    containIntrinsicBlockSize: "116px",
                    boxShadow: "0 1px 0 var(--color-border-default)",
                  }}
                  className={`cursor-pointer transition-colors ${
                    bulkEditMode && isSelected(fileIndex)
                      ? "bg-accent-primary/10"
                      : bulkEditMode && highlightedRowIndex === rowIndex
                        ? "bg-bg-hover"
                        : ""
                  } ${bulkEditMode ? "hover:bg-bg-hover" : ""}`}
                  onMouseEnter={() => {
                    if (bulkEditMode) {
                      setHighlightedRowIndex(rowIndex);
                    }
                  }}
                  onClick={(e) => handleRowClick(rowIndex, e)}
                >
                  {/* Checkbox cell - only visible in bulk edit mode */}
                  {bulkEditMode && (
                    <td className="w-10 px-2 py-2 text-center border-r border-border-default sticky left-0 z-10 bg-inherit">
                      <Checkbox
                        checked={isSelected(fileIndex)}
                        onChange={(e) => handleCheckboxChange(rowIndex, e)}
                        size="sm"
                      />
                    </td>
                  )}
                  <td className="p-2 border-r border-border-default min-w-[132px]">
                    <div className="flex justify-center">
                      <Thumbnail
                        src={file.thumbnailUrl}
                        alt={file.name}
                        size="preview"
                        onClick={() => handleThumbnailClick(fileIndex)}
                        title="Click to open in editor"
                      />
                    </div>
                  </td>
                  <td className="px-4 py-2 border-r border-border-default text-[12px] text-text-primary font-mono truncate max-w-[200px]">
                    {file.name}
                  </td>
                  {enabledFields.map((field) => {
                    const isFocused =
                      focusedCell?.rowIndex === rowIndex &&
                      focusedCell?.fieldId === field.id;
                    const cellState = cellStates.get(
                      `${rowIndex}-${field.id}`,
                    ) || {
                      isInSelection: false,
                      selectionEdges: null,
                      copiedEdges: null,
                    };
                    const { isInSelection, copiedEdges, selectionEdges } =
                      cellState;
                    const isEditing =
                      editingCell?.rowIndex === rowIndex &&
                      editingCell?.field === field.id;
                    const editSession = isEditing ? editingCell : null;

                    return (
                      <TableCell
                        key={field.id}
                        rowIndex={rowIndex}
                        fieldId={field.id}
                        isFocused={isFocused}
                        isInSelection={isInSelection}
                        copiedEdges={copiedEdges}
                        selectionEdges={selectionEdges}
                        isEditing={isEditing}
                        onMouseDown={(event) => {
                          if (!isEditing && event.shiftKey) {
                            event.preventDefault();
                          }
                        }}
                        onClick={(event) => {
                          if (!isEditing) {
                            handleCellClick(rowIndex, field.id, event);
                          }
                        }}
                      >
                        <EditableCell
                          field={field}
                          value={file.modifiedExifData[field.id]}
                          editSession={editSession}
                          isModified={isFieldModified(file, field.id)}
                          onSave={(newValue) =>
                            handleCellSave(rowIndex, field.id, newValue)
                          }
                          onCancel={() => exitEditMode(rowIndex, field.id)}
                        />
                      </TableCell>
                    );
                  })}

                  <td className="px-4 py-2 border-r border-border-default text-center align-middle">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-destructive hover:text-destructive hover:bg-destructive/10"
                      disabled={isDemo}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (isDemo) return;
                        if (confirm("Remove this file?")) {
                          removeFiles(new Set([fileIndex]));
                        }
                      }}
                      title={isDemo ? "Removing files is disabled in demo mode" : "Remove file"}
                    >
                      <X data-icon="inline-start" className="size-4" />
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div
        className={`border-t border-border-default text-xs text-text-secondary ${WORKSPACE_SPACING.statusStripInsetY} ${statusStripInsetXClass}`}
      >
        {tableFooterLabel}
      </div>

      <Dialog
        open={Boolean(confirmMeta)}
        onOpenChange={(open) => {
          if (!open) setConfirmAction(null);
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{confirmMeta?.title}</DialogTitle>
            <DialogDescription>{confirmMeta?.description}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfirmAction(null)}>
              Cancel
            </Button>
            <Button
              className={confirmMeta?.confirmClassName}
              onClick={() => {
                confirmMeta?.onConfirm?.();
                setConfirmAction(null);
              }}
            >
              {confirmMeta?.confirmLabel || "Confirm"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default TableView;
